import React, { createContext, useContext, useState, useTransition, useEffect, ReactNode } from "react";

interface RouteTransitionContextType {
  isPending: boolean;
  location: string;
  setLocation: (to: string, options?: { replace?: boolean }) => void;
}

const RouteTransitionContext = createContext<RouteTransitionContextType | null>(null);

export function useRouteTransition() {
  const context = useContext(RouteTransitionContext);
  if (!context) {
    throw new Error("useRouteTransition must be used within a RouteTransitionProvider");
  }
  return context;
}

export function useTransitionLocation() {
  const context = useContext(RouteTransitionContext);
  if (!context) {
    // Fallback if context is not available (e.g. testing)
    return [window.location.pathname, (to: string) => window.history.pushState(null, "", to)] as const;
  }
  return [context.location, context.setLocation] as const;
}

export function RouteTransitionProvider({ children }: { children: ReactNode }) {
  const [location, setLocationState] = useState(() => window.location.pathname);
  const [isPending, startTransition] = useTransition();

  useEffect(() => {
    const handlePopState = () => {
      startTransition(() => {
        setLocationState(window.location.pathname);
      });
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const setLocation = (to: string, options?: { replace?: boolean }) => {
    startTransition(() => {
      if (options?.replace) {
        window.history.replaceState(null, "", to);
      } else {
        window.history.pushState(null, "", to);
      }
      setLocationState(to);
    });
  };

  return (
    <RouteTransitionContext.Provider value={{ isPending, location, setLocation }}>
      {children}
    </RouteTransitionContext.Provider>
  );
}
